import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import {
    Button,
    ConfirmModal,
    DataTable,
    Icon,
    IconButton,
    Loading,
    Spacer,
    StyleFunction,
    Surface,
    useModal,
    usePagination,
    useSearch,
    useThemedStyle,
} from "@venuepos/react-common";
import { theme } from "@venuepos/react-common/src/theme/themes/venue-default";
import {
    GQLayoutEntitiesQuery,
    useLayoutEntitiesQuery,
    useLayoutEntityDeleteMutation,
} from "graphql-sdk";
import { formatDateTime, SearchDefinition } from "lib";
import { AvailableLocale } from "locales";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { useHandleMutationError, useSearchDefinition } from "../../hooks";
import { RootStackParamList } from "../../navigation";
import { useAdminSession } from "../../session";

export type CustomItem = {
    __typename: "CustomItem";
    externalId?: string;
};
export type BuyAccountFunds = {
    __typename: "BuyAccountFunds";
    externalId?: string;
};

export function LayoutEntityListScreen() {
    const { render } = useModal();
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);
    const { navigate } = useNavigation();

    const route = useRoute<RouteProp<RootStackParamList, "LAYOUT_ENTITIES">>();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
        initialSortDirection: "ASC",
    });
    const { createSearchDefinition } = useSearchDefinition();

    const defaultLayoutEntitySearch: SearchDefinition = {
        name: createSearchDefinition("name"),
        createdAt: createSearchDefinition("createdAt"),
    };

    const {
        component: searchComponent,
        indicator: searchIndicator,
        search,
    } = useSearch(defaultLayoutEntitySearch, {
        visible: true,
        onSubmit: () => {
            // When the search form is submitted, then return the pagination to the first page.
            onPageChange(0);
        },
    });

    const [deleteLayoutEntity] = useLayoutEntityDeleteMutation();
    const { handleMutationError } = useHandleMutationError();

    const { data, loading, refetch } = useLayoutEntitiesQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            searching: search,
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (route.params?.refetch) {
            refetch();
        }
    }, [refetch, route.params?.refetch]);

    const handleSelect = useCallback(
        (id: string) => {
            navigate("LAYOUT_ENTITY_EDIT", {
                id,
            });
        },
        [navigate]
    );

    const translateType = useCallback(
        (value: string) => {
            switch (value) {
                case "FUNCTION":
                    return t(
                        "backoffice.layout_entities.function.label",
                        "Function"
                    );
                default:
                    return value;
            }
        },
        [t]
    );

    const translateFunction = useCallback(
        (value: string) => {
            switch (value) {
                case "CUSTOM_ITEM":
                    return t(
                        "layout.function_button_form.types.custom_item",
                        "Custom item"
                    );
                case "BUY_ACCOUNT_FUNDS":
                    return t(
                        "layout.function_button_form.types.buy_account_funds",
                        "Buy account funds"
                    );
                default:
                    return value;
            }
        },
        [t]
    );

    const handleDelete = useCallback(
        async (id: string) => {
            const deleteResponse = await render(onClose => (
                <ConfirmModal
                    headerText={t(
                        "backoffice.layout_entities.delete",
                        "Delete layout entity?"
                    )}
                    bodyText={t(
                        "backoffice.layout_entities.delete_explain",
                        "Are you sure you want delete this layout entity?"
                    )}
                    onClose={onClose}
                />
            ));

            if (!deleteResponse) {
                return;
            }

            await handleMutationError(
                async () => await deleteLayoutEntity({ variables: { id } }),
                t(
                    "backoffice.layout_entities.deleted",
                    "Layout entity deleted"
                ),
                async () => {
                    await refetch();
                }
            );
        },
        [deleteLayoutEntity, handleMutationError, refetch, render, t]
    );

    const renderLayoutEntityRow = useCallback(
        (
            item: GQLayoutEntitiesQuery["layoutEntities"]["data"][0],
            index: number
        ) => (
            <DataTable.Row
                key={`layoutEntity_${index}`}
                style={index % 2 ? theme.styles.oddRow : null}
                onPress={() => handleSelect(item.id)}
            >
                <DataTable.Cell>{item.name}</DataTable.Cell>
                <DataTable.Cell>{translateType(item.type)}</DataTable.Cell>
                <DataTable.Cell>
                    {translateFunction(item.function)}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.createdAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.iconColumn}>
                    <IconButton
                        color={theme.colors.secondary}
                        name="edit"
                        onPress={() => handleSelect(item.id)}
                    />
                    <IconButton
                        color={theme.colors.secondary}
                        name="delete"
                        onPress={() => handleDelete(item.id)}
                    />
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            handleDelete,
            handleSelect,
            locale,
            styles.iconColumn,
            translateFunction,
            translateType,
        ]
    );

    return (
        <View>
            <View style={[theme.styles.row, styles.container]}>
                <Button
                    onPress={() => {
                        navigate("LAYOUT_ENTITY_CREATE");
                    }}
                    style={styles.button}
                >
                    {t("backoffice.layout_entities.create", "Create")}
                </Button>
                <Spacer space={1} />
                {searchIndicator}
            </View>
            <Spacer space={2} />
            <Surface>
                {searchComponent}
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "name" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("name")}
                        >
                            {t(
                                "backoffice.layout_entities.name.header",
                                "Name"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "type" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("type")}
                        >
                            {t(
                                "backoffice.layout_entities.type.header",
                                "Type"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "function"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("function")}
                        >
                            {t(
                                "backoffice.layout_entities.function.header",
                                "Function"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "createdAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("createdAt")}
                        >
                            {t(
                                "backoffice.layout_entities.created_at.header",
                                "Created at"
                            )}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.iconColumn}>
                            <Icon name="sort" color={theme.colors.black} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {!loading ? (
                        data?.layoutEntities.data.map(renderLayoutEntityRow)
                    ) : (
                        <Loading />
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.layoutEntities.pagination.pages}
                        itemCount={data?.layoutEntities.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </View>
    );
}

const styleFunc: StyleFunction = () => ({
    container: {
        justifyContent: "flex-end",
        flexWrap: "wrap",
    },
    iconColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 40 },
    smallColumn: {
        flexBasis: 140,
        flexGrow: 0,
    },
});
